// import hooks
import { useState } from 'react';

// import types
import type { CustomDutyLine } from 'RestAPIs/employees/hours-table/types';
import type { useCopyPasteParams } from './useCopyPaste.types';

// import utils
import { buildCreateValue, toOptionalNumber } from '../HoursTable.utils';
import { GridRowModes } from '@mui/x-data-grid-pro';

export function useCopyPaste({
  apiRef,
  setProcessedRows,
  employeeId,
  setExtraRows,
  rowModesModel
}: useCopyPasteParams) {
  'use memo';
  const [copyMode, setCopyMode] = useState(false);
  const [copySourceRowId, setCopySourceRowId] = useState<
    string | number | null
  >(null);
  const [copyPayload, setCopyPayload] =
    useState<Partial<CustomDutyLine> | null>(null);
  const [pastedRows, setPastedRows] = useState<Set<string | number>>(new Set());
  const [overrides, setOverrides] = useState<
    Map<string | number, Partial<CustomDutyLine>>
  >(new Map());

  const clearRow = (row: CustomDutyLine) => {
    const keepRow = new Set(['id', 'weekNumber', 'date', 'dateKey']);

    const cleared = {
      id: row.id,
      requestId: undefined, // turn it into a draft
      ...Object.fromEntries(
        Object.keys(row).map((key) => [key, keepRow.has(key) ? row[key] : null])
      )
    };

    // update the rendered grid immediately
    apiRef.current.updateRows([cleared]);

    // remember the cleared state for future renders
    setOverrides((prev) => new Map(prev).set(row.id, cleared));

    setProcessedRows?.((prev) => {
      const next = { ...prev };
      next.createdRows.delete(row.id);
      return next;
    });
  };

  // user clicks “paste” on a row
  const pasteOntoRow = (target: CustomDutyLine) => {
    if (!copyMode || !copyPayload) return;
    const copiedRow = apiRef.current.getRow(copySourceRowId);
    const prevRequestId = target.requestId ?? undefined;
    setPastedRows((prev) => new Set(prev).add(target.id));

    // when target is a negative id, it means that the row is a duplicate row
    const merged = {
      ...target,
      ...copyPayload,
      ...(copiedRow.brId && { brId: copiedRow.brId }),
      ...(copiedRow.dutyId && { dutyId: copiedRow.dutyId }),
      ...(copiedRow.dutyId && { displayDutyCode: copiedRow.displayDutyCode }),
      ...(copiedRow.flags && { flags: copiedRow.flags }),
      ...(copiedRow.waId && { waId: copiedRow.waId }),
      ...(copiedRow.periods && { periods: copiedRow.periods }),
      ...(copiedRow.finEmpKmManual && {
        finEmpKmManual: toOptionalNumber(copiedRow.finEmpKmManual)
      }),
      ...(copiedRow.finEmpTravelExpensesManual && {
        finEmpTravelExpensesManual: toOptionalNumber(
          copiedRow.finEmpTravelExpensesManual
        )
      }),
      requestId: null,
      originalRequestId: prevRequestId
    };

    // add the row to the extra rows if it is a duplicate empty row
    if (target.requestId == null) {
      setExtraRows?.((prev) =>
        prev.map((row) => (row.id === target.id ? merged : row))
      );
    }

    apiRef.current.updateRows([merged]);
    setOverrides((prev) => new Map(prev).set(target.id, merged));

    // bookkeeping so flushBatch gets correct toCreate / toUpdate
    setProcessedRows?.((prev) => {
      const next = { ...prev };
      if (prevRequestId !== undefined) {
        next.deletedRows.add(prevRequestId);
      }
      next.createdRows.set(
        merged.id,
        buildCreateValue(merged, employeeId, merged.dateKey)
      );
      next.updatedRows.delete(merged.id);
      return next;
    });
  };

  const undoPaste = (row: CustomDutyLine) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.delete(row.id);
      return next;
    });
    setPastedRows((prev) => {
      const next = new Set(prev);
      next.delete(row.id);
      return next;
    });

    setProcessedRows?.((prev) => {
      const next = { ...prev };
      next.createdRows.delete(row.id);
      if ((row as any).originalRequestId) {
        next.deletedRows.delete((row as any).originalRequestId);
      }
      return next;
    });
  };

  const resetCopyState = () => {
    // ignoreModifications at other row(s) on cancel
    apiRef.current.getAllRowIds().forEach((id: number) => {
      if (rowModesModel[id]?.mode === GridRowModes.Edit) {
        apiRef.current.stopRowEditMode({
          id: id,
          ignoreModifications: true
        });
      }
    });

    // reset all states
    setCopyMode(false);
    setCopySourceRowId(null);
    setCopyPayload(null);
    setPastedRows(new Set());
    setOverrides(new Map());
    setProcessedRows &&
      setProcessedRows({
        createdRows: new Map(),
        updatedRows: new Map(),
        deletedRows: new Set()
      });
  };

  return {
    // state
    copyMode,
    copySourceRowId,
    copyPayload,
    pastedRows,
    overrides,
    // actions
    setCopyMode,
    setCopySourceRowId,
    setCopyPayload,
    pasteOntoRow,
    undoPaste,
    resetCopyState,
    clearRow,
    setOverrides
  };
}
